/**
 * App Component
 * 
 * Root component that sets up:
 * - React Router with all application routes
 * - Error boundary for error handling
 * - Context providers (PlayerProvider)
 * - Responsive navigation with hamburger menu
 * 
 * Requirements: 6.1, 6.2, 6.3, 7.1, 7.2, 7.3, 7.4, 12.2
 */

import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PlayerProvider } from './contexts/PlayerProvider';
import { NavigationProvider } from './contexts/NavigationProvider';
import { LoadingSpinner } from './components/LoadingSpinner';
import './App.css';

// Lazy load route components for code splitting
// Requirements: 11.2, 11.3
const HomePage = lazy(() => import('./pages/HomePage'));
const StoryPage = lazy(() => import('./pages/StoryPage'));
const EpisodePage = lazy(() => import('./pages/EpisodePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/**
 * AppContent Component
 * 
 * Contains the routes.
 * Separated from App to allow useLocation hook inside BrowserRouter.
 * 
 * Uses Suspense to handle lazy-loaded route components.
 * Requirements: 11.2, 11.3
 */
const AppContent: React.FC = () => {
  return (
    <div className="app">
      <main className="app-main" role="main">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Root redirect to language-specific home */}
            <Route path="/" element={<HomePage />} />
            
            {/* Language-specific Home Route */}
            <Route path="/:language" element={<HomePage />} />
            
            {/* Story Route with Language - using slug */}
            <Route path="/:language/:storySlug" element={<StoryPage />} />
            
            {/* Episode Detail Route with Language - using slugs */}
            <Route path="/:language/:storySlug/:episodeSlug" element={<EpisodePage />} />
            
            {/* Legacy Routes (redirect to language-specific) */}
            <Route path="/story/:storyId" element={<StoryPage />} />
            <Route path="/story/:storyId/episode/:episodeId" element={<EpisodePage />} />
            <Route path="/episode/:id" element={<EpisodePage />} />
            <Route path="/:language/story/:storyId" element={<StoryPage />} />
            <Route path="/:language/story/:storyId/episode/:episodeId" element={<EpisodePage />} />
            
            {/* 404 Not Found Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
};

/**
 * App Component
 * 
 * Root component that wraps the application with:
 * 1. ErrorBoundary - catches and handles React errors
 * 2. PlayerProvider - manages audio playback state
 * 3. BrowserRouter - enables client-side routing
 * 4. NavigationProvider - centralizes navigation logic
 * 
 * Requirements: 6.1, 6.2, 6.3, 7.1, 7.2, 7.3, 7.4, 12.2
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <PlayerProvider>
        <BrowserRouter>
          <NavigationProvider>
            <AppContent />
          </NavigationProvider>
        </BrowserRouter>
      </PlayerProvider>
    </ErrorBoundary>
  );
};

export default App;
